import React, { useCallback, useContext, useMemo } from 'react';

import classNames from 'classnames';

import './RadioGroup.scss';
import { useUniqueId } from '../hooks/useUniqueId';

type RadioGroupContext = {
    readonly value: string | null;
    readonly onChange: (newValue: string) => void;
};

const DEFAULT_ONCHANGE = (newValue: string) => {
    if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.warn(`unhandled radio selection: ${newValue}`);
    }
};

const Ctx = React.createContext<RadioGroupContext>({ value: null, onChange: DEFAULT_ONCHANGE });

type RadioGroupProps = {
    readonly value?: string | null;
    readonly onChange?: (newValue: string) => void;
    readonly className?: string;
    readonly children?: React.ReactNode;
    readonly isInline?: boolean;
};

export const RadioGroup = React.forwardRef<HTMLUListElement, RadioGroupProps>((props, ref) => {
    const { value, onChange, className, children, isInline = true } = props;

    const ctx = useMemo(() => {
        return {
            value: value ?? null,
            onChange: onChange ?? DEFAULT_ONCHANGE
        };
    }, [value, onChange]);

    return (
        <Ctx.Provider value={ctx}>
            <ul className={classNames('radio-group', { 'radio-group--inline': isInline }, className)} ref={ref}>
                {React.Children.map(children, (child, index) => (
                    <li key={index}>{child}</li>
                ))}
            </ul>
        </Ctx.Provider>
    );
});

type RadioButtonProps = {
    readonly value: string;
    readonly className?: string;
    readonly selectedClassName?: string;
    readonly children?: React.ReactNode;
    readonly disabled?: boolean;
};

export const RadioButton = React.forwardRef<HTMLInputElement, RadioButtonProps>((props, ref) => {
    const { value, className, selectedClassName, children, disabled } = props;
    const { value: selectedValue, onChange } = useContext(Ctx);
    const selected = value === selectedValue;
    const inputId = useUniqueId('radio-group-input-');

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            if (e.currentTarget.checked) {
                onChange(value);
            }
        },
        [value, onChange]
    );

    return (
        <div className={classNames('radio-group__control', className, selected && selectedClassName)}>
            <input id={inputId} type="radio" ref={ref} checked={selected} onChange={handleChange} disabled={disabled} />
            <label htmlFor={inputId} className={disabled ? 'disabled' : ''}>
                {children}
            </label>
        </div>
    );
});

RadioGroup.displayName = 'RadioGroup';
RadioButton.displayName = 'RadioButton';
